import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import Loading from "../../../../components/Loading";
import { getRajSchedulingData } from "../../../../Rest_api/RestApi";
import { DynamicDatePicker } from "../../../../components/DatePicker";
import { getLast7DaysDateRange } from "../../const";

// Types
interface BlockData {
  id?: number;
  date: string;
  schedule_in: string;
  type_of_transaction: string;
  ppa_contract_capacity: number;
  rate: number;
  ruvitl_scheduling_request: number;
  actual_scheduling: number;
  transmission_loss: number;
  entity: number;
  slot: number;
  trader: number;
  entity__name: string;
}

// Header Cell Component
const HeaderCell: React.FC<{ title: string }> = ({ title }) => (
  <th className="py-3 text-[14px] px-4 border-b bg-[#FAFAFA] text-center font-medium text-[#6F6D7C] whitespace-nowrap">
    {title}
  </th>
);

// Data Cell Component
const DataCell: React.FC<{ value: string | number; className?: string }> = ({
  value,
  className,
}) => (
  <td
    className={`py-2 px-4 text-[12px] border-b text-[#8C8A96] ${className} ${
      typeof value == "number" ? "font-semibold text-[#34656D]" : ""
    }  `}
  >
    {value}
  </td>
);

// Contract Row Component
const ContractRow: React.FC<{
  contract: BlockData;
}> = ({ contract }) => (
  <tr className="hover:bg-gray-50">
    <DataCell value={contract.date} />
    <DataCell value={`${contract.slot}`} />
    <DataCell value={contract.schedule_in} />
    <DataCell value={contract.type_of_transaction} />
    <DataCell value={contract.trader} />
    <DataCell value={contract.entity__name} />
    <DataCell value={contract.ppa_contract_capacity} />
    <DataCell value={`${contract.rate}`} />
    <DataCell value={contract.ruvitl_scheduling_request} />
    <DataCell
      className={`${
        contract.ruvitl_scheduling_request === contract.actual_scheduling
          ? ""
          : "text-red-400"
      }`}
      value={contract.actual_scheduling}
    />
    <DataCell
      className={`${
        contract.ppa_contract_capacity === contract.transmission_loss
          ? ""
          : "text-red-400 font-semibold"
      }`}
      value={`${contract.transmission_loss}`}
    />
  </tr>
);

// Main Component
const BlockTable = () => {
  const [loadingFull, setLoadingFull] = useState(false);
  const [contracts, setContracts] = useState<BlockData[]>([]);
  const [dates, setDate] = useState(getLast7DaysDateRange());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");

  function Downlaod() {
    let aa = [...contracts].map((contract) => {
      // Check if values are mismatched
      const isRequestDifferentFromActual =
        contract.ppa_contract_capacity !== contract.transmission_loss;

      return {
        datetime: contract.date,
        time_block: `${contract.slot}`,
        schedule_in: contract.schedule_in,
        type_of_transaction: contract.type_of_transaction,
        trader: contract.trader,
        from_utility: contract.entity__name,
        ppa_contract_capacity_mw: `${contract.ppa_contract_capacity}`,
        rate: `${contract.rate}`,
        ruvitl_scheduling_request: `${contract.ruvitl_scheduling_request}`,
        value: `${contract.actual_scheduling}`,
        value_with_transmission_losses: `${contract.transmission_loss}`,
        status: isRequestDifferentFromActual ? "MISMATCH" : "MATCH",
      };
    });

    // Use Papa.unparse with specific configuration
    var csv = Papa.unparse(aa, {
      quotes: true, // Force quotes around all fields
      header: true,
    });

    var link = document.createElement("a");
    link.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv)
    );
    link.setAttribute("download", "Contract Scheduling.csv");
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  useEffect(() => {
    const handleFetch = async () => {
      setLoadingFull(true);
      const entity = await getRajSchedulingData({
        startDate: dates.startDate.toISOString().slice(0, 10),
        endDate: dates.endDate.toISOString().slice(0, 10),
      });

      // Sort the data first by entity name, then by date, and finally by slot number
      const sortedData = [...entity].sort((a, b) => {
        // First compare by entity (primary sort)
        const entityComparison = String(a.entity).localeCompare(
          String(b.entity)
        );

        if (entityComparison !== 0) {
          return entityComparison; // Different entities, sort by entity name
        }

        // If same entity, compare by date (secondary sort)
        const dateComparison =
          new Date(a.date).getTime() - new Date(b.date).getTime();

        if (dateComparison !== 0) {
          return dateComparison; // Different dates, sort chronologically
        }

        // If same entity and date, sort by slot number (tertiary sort)
        return a.slot - b.slot;
      });

      setContracts(sortedData);
      setCurrentPage(1);
      setLoadingFull(false);
    };

    handleFetch();
  }, [dates]);

  // Filter contracts based on search term
  const filteredContracts = contracts.filter((contract) =>
    Object.values(contract).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Get current contracts for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContracts = filteredContracts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Calculate total pages
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <div>
      <>{loadingFull && <Loading />}</>

      {/* Search and Items Per Page Controls */}
      <div className="my-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="relative mr-4">
            <input
              type="text"
              placeholder="Search..."
              className="pl-8 pr-4 py-2 border rounded-md"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
            <svg
              className="w-4 h-4 absolute left-2.5 top-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <select
            className="border rounded-md py-2 px-3"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing items per page
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>

        <div className="flex gap-4">
          <div>
            <DynamicDatePicker
              setDate={(date) => setDate(date[0]!)!}
              defaultDates={dates}
            />
          </div>

          <button onClick={Downlaod} className="downloadButton">
            Download{" "}
          </button>
        </div>
      </div>

      <div
        className="contract-table-container h-[calc(100vh-350px)] overflow-y-auto"
        style={{
          scrollbarWidth: "auto", // For Firefox
          scrollbarColor: "#d1d5db transparent", // For Firefox
        }}
      >
        <style>{`
        /* For Chrome, Safari, and newer Edge */
        div::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        
        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
        <table className="table-auto text-nowrap w-full">
          <thead>
            <tr>
              <HeaderCell title="Date" />
              <HeaderCell title="Time Block" />
              <HeaderCell title="Schedule In" />
              <HeaderCell title="Transaction Type" />
              <HeaderCell title="Trader" />
              <HeaderCell title="Entity" />
              <HeaderCell title="PPA Contract Capacity (MW)" />
              <HeaderCell title="Rate" />
              <HeaderCell title="RUVITL Request" />
              <HeaderCell title="Actual Scheduling" />
              <HeaderCell title="Value with Transmission Losses " />
            </tr>
          </thead>
          <tbody>
            {currentContracts.length > 0 ? (
              currentContracts.map((contract, index) => (
                <ContractRow key={index} contract={contract} />
              ))
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-4 text-gray-500">
                  {loadingFull
                    ? "Loading..."
                    : searchTerm
                    ? "No matching records found"
                    : "No data available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center p-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">
              {filteredContracts.length > 0 ? indexOfFirstItem + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(indexOfLastItem, filteredContracts.length)}
            </span>{" "}
            of <span className="font-medium">{filteredContracts.length}</span>{" "}
            entries
          </p>
        </div>

        <div className="flex space-x-1">
          {/* First Page Button */}
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1 || totalPages === 0}
            className={`px-3 py-1 rounded border ${
              currentPage === 1 || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <span className="sr-only">First</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
              <path
                fillRule="evenodd"
                d="M8.707 5.293a1 1 0 010 1.414L5.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Previous Page Button */}
          <button
            onClick={() => paginate(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || totalPages === 0}
            className={`px-3 py-1 rounded border ${
              currentPage === 1 || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <span className="sr-only">Previous</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Page Numbers */}
          {[...Array(totalPages)].map((_, index) => {
            // Show current page and 1 page before and after
            if (
              index === 0 ||
              index === totalPages - 1 ||
              (index >= currentPage - 2 && index <= currentPage)
            ) {
              return (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  className={`px-3 py-1 rounded border ${
                    currentPage === index + 1
                      ? "bg-primary text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {index + 1}
                </button>
              );
            }
            // Show ellipsis if there's a gap
            if (index === 1 || index === totalPages - 2) {
              return (
                <span
                  key={index}
                  className="px-3 py-1 border rounded bg-white text-gray-600"
                >
                  ...
                </span>
              );
            }
            return null;
          })}

          {/* Next Page Button */}
          <button
            onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`px-3 py-1 rounded border ${
              currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <span className="sr-only">Next</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Last Page Button */}
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`px-3 py-1 rounded border ${
              currentPage === totalPages || totalPages === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <span className="sr-only">Last</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
              <path
                fillRule="evenodd"
                d="M11.293 14.707a1 1 0 010-1.414L14.586 10l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlockTable;
